import { useState } from "react";
import { toast } from "sonner";
import { Link2, Copy, RefreshCw, Send, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, copyText, fmtDateTime } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { portalLink } from "@/lib/portal";
import { Panel } from "@/components/core/AppLayout";
import { ConfirmDialog } from "@/components/core/ConfirmDialog";

const SendBox = ({ client, onSent }) => {
  const [to, setTo] = useState(client.email || "");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clients/${client.id}/portale/invia`, { to: to.split(/[,;\s]+/).filter(Boolean), messaggio: msg });
      toast.success("Link inviato al cliente"); onSent(r.data); setMsg("");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 mt-6">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Invia il link via email</div>
      <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="email@cliente.it (separa con virgola)" className="rounded-xl" data-testid="portal-send-to" />
      <Textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Messaggio (facoltativo)" className="rounded-xl" data-testid="portal-send-message" />
      <Button onClick={send} disabled={busy} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="portal-send-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}Invia link</Button>
    </div>
  );
};

export const ClientPortal = ({ client }) => {
  const { data: p, setData } = useFetch(`/clients/${client.id}/portale`);
  const [regen, setRegen] = useState(false);
  const generate = async () => {
    try { setData((await api.post(`/clients/${client.id}/portale/genera`)).data); toast.success(p?.exists ? "Nuovo link generato: il precedente non funziona più" : "Portale creato"); }
    catch (e) { toast.error(errMsg(e)); }
    setRegen(false);
  };
  const update = async (patch) => {
    try { setData((await api.patch(`/clients/${client.id}/portale`, { active: p.active, expires_at: p.expires_at, ...patch })).data); toast.success("Portale aggiornato"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!p) return null;
  if (!p.exists) return (
    <Panel className="p-8 max-w-3xl" data-testid="client-portal">
      <h3 className="font-display text-xl font-extrabold text-[#0A1128] flex items-center gap-2 mb-2"><Link2 className="h-5 w-5 text-[#E8173A]" />Portale cliente</h3>
      <p className="text-sm text-slate-600 mb-5">Crea un link privato con cui il cliente approva i post «In revisione» e carica materiali, senza login.</p>
      <Button onClick={generate} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="portal-generate-button">Genera link privato</Button>
    </Panel>
  );
  const link = portalLink(p.path);
  return (
    <Panel className="p-8 max-w-3xl" data-testid="client-portal">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-extrabold text-[#0A1128] flex items-center gap-2"><Link2 className="h-5 w-5 text-[#E8173A]" />Portale cliente</h3>
        <label className="flex items-center gap-2 text-sm font-bold text-[#0A1128]">{p.active ? "Attivo" : "Disattivato"}
          <Switch checked={p.active} onCheckedChange={(v) => update({ active: v })} className="data-[state=checked]:bg-[#E8173A] data-[state=unchecked]:bg-[#5FB3EA]" data-testid="portal-active-toggle" />
        </label>
      </div>
      {p.scaduto && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-[#C8102E]" data-testid="portal-expired">Il link è scaduto: modifica la scadenza o rigeneralo.</div>}
      <div className="flex gap-2">
        <Input readOnly value={link} className="rounded-xl font-mono text-xs" data-testid="portal-link-input" />
        <Button variant="outline" onClick={async () => { await copyText(link); toast.success("Link copiato"); }} className="rounded-full font-bold" data-testid="portal-copy-button"><Copy className="h-4 w-4 mr-1.5" />Copia</Button>
        <a href={link} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full border px-3 text-[#0A1128] hover:bg-[#A6DAFC]" data-testid="portal-open-link"><ExternalLink className="h-4 w-4" /></a>
      </div>
      <div className="flex flex-wrap items-end gap-4 mt-4">
        <div className="space-y-1"><div className="text-xs font-bold uppercase tracking-wider text-slate-600">Scadenza (facoltativa)</div>
          <Input type="date" value={p.expires_at || ""} onChange={(e) => update({ expires_at: e.target.value || null })} className="rounded-xl w-48" data-testid="portal-expiry-input" /></div>
        <Button variant="outline" onClick={() => setRegen(true)} className="rounded-full font-bold text-[#E8173A] border-red-200" data-testid="portal-regenerate-button"><RefreshCw className="h-4 w-4 mr-1.5" />Rigenera link</Button>
      </div>
      <SendBox client={client} onSent={setData} />
      {p.invii?.length > 0 && <div className="mt-5 text-xs text-slate-600 space-y-1" data-testid="portal-send-history">{[...p.invii].reverse().map((i) => <div key={i.id}>Inviato il {fmtDateTime(i.sent_at)} a {i.to.join(", ")} da {i.sent_by_nome}</div>)}</div>}
      <ConfirmDialog open={regen} onOpenChange={setRegen} title="Rigenerare il link?" description="Il link attuale smetterà subito di funzionare. Dovrai inviare il nuovo link al cliente." onConfirm={generate} />
    </Panel>
  );
};
